import React from 'react';

import { TDashboardPageData, TDashboardTableScreenMap } from 'types/state';
import { useIntlDictionary } from 'hooks/useIntlDictionary';
import { useScreenMap } from 'hooks/useScreenMap';
import { useUserRoles } from 'hooks/useUserRole';
import { DashboardTableColumn } from 'containers/DashboardTable/DashboardTableColumn';
import { DashboardTableRow } from 'containers/DashboardTable/DashboardTableRow';
import { DashboardTableWrapper } from 'containers/DashboardTable/DashboardTableWrapper';
import { DashboardTableCell } from 'containers/DashboardTable/DashboardTableCell';
import { DashboardTableLabel } from 'containers/DashboardTable/DashboardTableLabel';
import { DashboardPasswordEditor } from 'containers/DashboardPasswordEditor';
import { routes } from 'utils/routes';
import { Link } from 'react-router-dom';
import { TextComponent } from 'design/text/textComponents';
import { LinkControlComponent } from 'design/linkControl';
import { ClickControlComponent } from 'design/clickControl/clickControlComponents';

type TProps = {
  content: TDashboardPageData;
  onContentEdited(): void;
};

export const DashboardTable = (props: TProps) => {
  const { content } = props;

  const intl = useIntlDictionary();

  // Подключает роль авторизованного пользователя.
  const userRoles = useUserRoles();

  // Состояние экрана.
  const { screenMap, enableScreen, disableScreen } = useScreenMap<
    TDashboardTableScreenMap
  >({
    DEMO_PENDING: false,
    DEMO_FAILED: false,
    DEMO_SUCCEEDED: false,
    EDIT_PROFILE: false,
    EDIT_LOGIN: false,
    EDIT_PASSWORD: false,
  });

  // Обработчик намерения отредактировать пароль.
  const onChangePassword = React.useCallback(() => {
    enableScreen('EDIT_PASSWORD');
  }, [enableScreen]);

  // Обработчик успешной отправки отредактированного пароля.
  const onPasswordEditorSubmitSucceeded = React.useCallback(() => {
    disableScreen('EDIT_PASSWORD');
  }, [disableScreen]);

  // Обработчик намерения отменнить редактирование пароля.
  const onPasswordEditorClose = React.useCallback(() => {
    disableScreen('EDIT_PASSWORD');
  }, [disableScreen]);

  const {
    accountInfo,
    demoStackInfo,
    prodStackStatus,
    prodStackInfo,
  } = content;

  return (
    <DashboardTableWrapper>
      {/* Demo stack block */}
      {userRoles.includes('CLIENT') && demoStackInfo && (
        <DashboardTableRow title={intl.dashboardRowDemoStackTitle}>
          <DashboardTableColumn>
            <DashboardTableCell>
              <LinkControlComponent to={demoStackInfo.url} newTab>
                <TextComponent color="primary" size="m">
                  {demoStackInfo.dnsName}
                </TextComponent>
              </LinkControlComponent>
            </DashboardTableCell>
          </DashboardTableColumn>
          <DashboardTableColumn>
            <DashboardTableCell>
              <DashboardTableLabel text={intl.dashboardDemoStackActiveHint} />
            </DashboardTableCell>
          </DashboardTableColumn>
        </DashboardTableRow>
      )}
      {userRoles.includes('CLIENT') && !demoStackInfo && (
        <DashboardTableRow title={intl.dashboardRowDemoStackTitle}>
          <DashboardTableColumn>
            <DashboardTableCell>
              <Link to={routes.registrationDemo()} target="_blank">
                <TextComponent color="primary" size="m">
                  {intl.dashboardCreateDemoAction}
                </TextComponent>
              </Link>
            </DashboardTableCell>
          </DashboardTableColumn>
          <DashboardTableColumn>
            <DashboardTableCell>
              {
                <DashboardTableLabel
                  text={intl.dashboardDemoStackMissingHint}
                />
              }
            </DashboardTableCell>
          </DashboardTableColumn>
        </DashboardTableRow>
      )}
      {/* Prod stack block */}
      {userRoles.includes('CLIENT') &&
        prodStackStatus === 'ACTIVE' &&
        prodStackInfo && (
          <DashboardTableRow title={intl.dashboardRowProdStackTitle}>
            <DashboardTableColumn>
              <DashboardTableCell>
                <LinkControlComponent to={prodStackInfo.url} newTab>
                  <TextComponent color="primary" size="m">
                    {prodStackInfo.url}
                  </TextComponent>
                </LinkControlComponent>
              </DashboardTableCell>
            </DashboardTableColumn>
          </DashboardTableRow>
        )}
      {userRoles.includes('CLIENT') && prodStackStatus !== 'ACTIVE' && (
        <DashboardTableRow title={intl.dashboardRowProdStackTitle}>
          <DashboardTableColumn>
            {prodStackStatus === 'MISSING' && (
              <DashboardTableCell>
                <Link to={routes.registrationProd()} target="_blank">
                  <TextComponent color="primary" size="m">
                    {intl.dashboardCreateProdAction}
                  </TextComponent>
                </Link>
              </DashboardTableCell>
            )}
            {prodStackStatus !== 'MISSING' && accountInfo && (
              <DashboardTableCell>
                <DashboardTableLabel
                  text={intl.dashboardProdStackPendingLabel}
                />
              </DashboardTableCell>
            )}
          </DashboardTableColumn>
          <DashboardTableColumn>
            {prodStackStatus === 'MISSING' && (
              <DashboardTableCell>
                <DashboardTableLabel
                  text={intl.dashboardProdStackMissingHint}
                />
              </DashboardTableCell>
            )}
            {prodStackStatus !== 'MISSING' && accountInfo && (
              <DashboardTableCell>
                <DashboardTableLabel
                  text={intl.dashboardProdStackPendingHint(accountInfo.email)}
                />
              </DashboardTableCell>
            )}
          </DashboardTableColumn>
        </DashboardTableRow>
      )}

      {/* Account block */}
      {accountInfo && (
        <DashboardTableRow title={intl.dashboardRowAccountTitle}>
          <DashboardTableColumn>
            <DashboardTableCell>
              <DashboardTableLabel primary text={intl.dashboardEmailLabel} />
              <DashboardTableLabel text={accountInfo.email} />
            </DashboardTableCell>
          </DashboardTableColumn>

          <DashboardTableColumn>
            <DashboardTableCell>
              <ClickControlComponent onClick={onChangePassword}>
                <TextComponent color="accent">
                  {intl.dashboardChangePasswordAction}
                </TextComponent>
              </ClickControlComponent>
            </DashboardTableCell>
            {screenMap.EDIT_PASSWORD && (
              <DashboardPasswordEditor
                onSubmitSucceeded={onPasswordEditorSubmitSucceeded}
                onClose={onPasswordEditorClose}
              />
            )}
          </DashboardTableColumn>
        </DashboardTableRow>
      )}
    </DashboardTableWrapper>
  );
};
